/**
 * BlockAwarePlayCanvas
 *
 * A wrapper component that bridges the usePlayBlocks hook to BaseCanvas.
 * Receives block-based data and mutation callbacks, converts them to
 * BaseCanvas callback format.
 *
 * Phase 6: Page Integration
 */

import React, { useMemo, useCallback } from 'react'
import { BaseCanvas } from './BaseCanvas'
import { CanvasDialogs } from './CanvasDialogs'
import type { Player } from '@/types/Player.types'
import type { Drawing, DrawingState, HashAlignment } from '@/types'
import type { BaseCanvasCallbacks, BaseCanvasState, BaseCanvasActions } from './BaseCanvas.types'

export interface BlockAwarePlayCanvasProps {
	/** Players from usePlayBlocks */
	players: Player[]
	/** Drawings from usePlayBlocks */
	drawings: Drawing[]
	/** Drawing state from usePlayBlocks */
	drawingState: DrawingState
	/** Currently selected object IDs */
	selectedObjectIds: string[]
	/** Called when selection changes */
	onSelectionChange: (ids: string[]) => void
	/** Called when a player is moved */
	onPlayerMove: (id: string, x: number, y: number) => void
	/** Called when a player is added - returns server-assigned ID */
	onPlayerAdd: (player: Omit<Player, 'id'>) => Promise<string>
	/** Called when a player is deleted */
	onPlayerDelete: (id: string) => void
	/** Called when a drawing is added */
	onDrawingAdd: (drawing: Omit<Drawing, 'id'>) => void
	/** Called when a drawing is updated */
	onDrawingUpdate: (id: string, updates: Partial<Drawing>) => void
	/** Called when a drawing is deleted */
	onDrawingDelete: (id: string) => void
	/** Called when a control point is moved (persists to server) */
	onControlPointUpdate?: (drawingId: string, pointId: string, x: number, y: number) => void
	/** Called when a player label is changed */
	onPlayerLabelChange?: (id: string, label: string) => void
	/** Called when a drawing is linked to a player (persists to server) */
	onLinkDrawingToPlayer?: (drawingId: string, pointId: string, playerId: string) => void
	/** Called when a drawing is unlinked from a player (persists to server) */
	onUnlinkDrawingFromPlayer?: (drawingId: string) => void
	/** Hash alignment for field positioning */
	hashAlignment?: HashAlignment
	/** Whether the canvas is readonly */
	readonly?: boolean
	/** Animation overlay rendered inside the field viewport */
	animationOverlay?: React.ReactNode
}

/**
 * BlockAwarePlayCanvas
 *
 * Bridges usePlayBlocks data to BaseCanvas callbacks.
 */
export function BlockAwarePlayCanvas({
	players,
	drawings,
	drawingState,
	selectedObjectIds: _selectedObjectIds,
	onSelectionChange,
	onPlayerMove,
	onPlayerAdd,
	onPlayerDelete,
	onDrawingAdd,
	onDrawingUpdate,
	onDrawingDelete,
	onControlPointUpdate,
	onPlayerLabelChange,
	onLinkDrawingToPlayer,
	onUnlinkDrawingFromPlayer,
	hashAlignment = 'middle',
	readonly = false,
	animationOverlay,
}: BlockAwarePlayCanvasProps) {
	// Convert single selection callback to match BaseCanvas format
	const handleSelectionChange = useCallback((id: string | null) => {
		if (id === null) {
			onSelectionChange([])
		} else {
			onSelectionChange([id])
		}
	}, [onSelectionChange])

	// Build callbacks for BaseCanvas
	const callbacks = useMemo<BaseCanvasCallbacks>(() => ({
		// Drawing operations
		onDrawingsChange: (_newDrawings: Drawing[]) => {
			// Diff detection would go here for more granular updates
			// For now, this is used by SVGCanvas internally
		},
		onDrawingAdd: (drawing: Drawing) => {
			onDrawingAdd(drawing)
		},
		onDrawingUpdate: (id: string, updates: Partial<Drawing>) => {
			onDrawingUpdate(id, updates)
		},
		onDrawingDelete: (id: string) => {
			onDrawingDelete(id)
		},

		// Control point operations
		onControlPointUpdate: onControlPointUpdate
			? (drawingId: string, pointId: string, x: number, y: number) => {
					onControlPointUpdate(drawingId, pointId, x, y)
				}
			: undefined,

		// Player operations
		onPlayersChange: (_newPlayers: Player[]) => {
			// Diff detection would go here for more granular updates
		},
		onPlayerAdd: async (player: Player): Promise<string> => {
			return onPlayerAdd(player)
		},
		onPlayerMove: (id: string, x: number, y: number) => {
			onPlayerMove(id, x, y)
		},
		onPlayerDelete: (id: string) => {
			onPlayerDelete(id)
		},
		onPlayerFill: (id: string, color: string) => {
			// Fill is an update operation
			onDrawingUpdate(id, { style: { color } } as Partial<Drawing>)
		},
		onPlayerLabelChange: (id: string, label: string) => {
			if (onPlayerLabelChange) {
				onPlayerLabelChange(id, label)
			}
		},

		// Linking operations
		onLinkDrawingToPlayer: (drawingId: string, pointId: string, playerId: string) => {
			if (onLinkDrawingToPlayer) {
				onLinkDrawingToPlayer(drawingId, pointId, playerId)
			}
		},
		onUnlinkDrawingFromPlayer: (drawingId: string) => {
			if (onUnlinkDrawingFromPlayer) {
				onUnlinkDrawingFromPlayer(drawingId)
			}
		},

		// Pre-snap operations
		onPreSnapUpdate: (drawingId: string, preSnapMotion) => {
			onDrawingUpdate(drawingId, { preSnapMotion } as Partial<Drawing>)
		},
	}), [onPlayerMove, onPlayerAdd, onPlayerDelete, onPlayerLabelChange, onDrawingAdd, onDrawingUpdate, onDrawingDelete, onControlPointUpdate, onLinkDrawingToPlayer, onUnlinkDrawingFromPlayer])

	// Render dialogs via render prop
	const renderDialogs = useCallback((state: BaseCanvasState, actions: BaseCanvasActions) => {
		return (
			<CanvasDialogs
				state={state}
				actions={actions}
				players={players}
				drawings={drawings}
				callbacks={callbacks}
			/>
		)
	}, [players, drawings, callbacks])

	return (
		<BaseCanvas
			drawingState={drawingState}
			drawings={drawings}
			players={players}
			callbacks={callbacks}
			hashAlignment={hashAlignment}
			readonly={readonly}
			renderDialogs={renderDialogs}
			onSelectionChange={handleSelectionChange}
			animationOverlay={animationOverlay}
		/>
	)
}
